import React, { useState, useEffect, useRef } from 'react';
import { facilityProfileAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import AddressAutocomplete from '@/components/AddressAutocomplete';
import {
  Building2,
  Camera,
  Image,
  Video,
  Users,
  Clock,
  Plus,
  Trash2,
  Upload,
  Loader2,
  Save,
  X,
  Mail,
  CheckCircle,
  XCircle,
  Stethoscope,
  MapPin,
  Phone,
  Globe,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const MEDICAL_SPECIALTIES = [
  "Médecine générale", "Pédiatrie", "Gynécologie-Obstétrique", "Cardiologie",
  "Dermatologie", "Ophtalmologie", "ORL", "Neurologie", "Psychiatrie",
  "Chirurgie générale", "Orthopédie", "Urologie", "Gastro-entérologie",
  "Pneumologie", "Rhumatologie", "Endocrinologie", "Néphrologie",
  "Oncologie", "Radiologie", "Anesthésie-Réanimation", "Urgences",
  "Médecine interne", "Gériatrie", "Kinésithérapie", "Dentisterie",
  "Laboratoire", "Pharmacie", "Imagerie médicale"
];

const DAYS = [
  { key: 'monday', label: 'Lundi' },
  { key: 'tuesday', label: 'Mardi' },
  { key: 'wednesday', label: 'Mercredi' },
  { key: 'thursday', label: 'Jeudi' },
  { key: 'friday', label: 'Vendredi' },
  { key: 'saturday', label: 'Samedi' },
  { key: 'sunday', label: 'Dimanche' }
];

const FacilityProfilePage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [facility, setFacility] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [invitations, setInvitations] = useState([]);
  
  // Refs for file inputs
  const profilePhotoRef = useRef(null);
  const coverPhotoRef = useRef(null);
  const galleryRef = useRef(null);
  
  // Form states
  const [basicInfo, setBasicInfo] = useState({
    name: '', description: '', phone: '', email: '', website: '', address: '', city: ''
  });
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [openingHours, setOpeningHours] = useState({});
  const [emergency24h, setEmergency24h] = useState(false);
  const [alwaysOpen, setAlwaysOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  
  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const [profileRes, profsRes, invitesRes] = await Promise.all([
        facilityProfileAPI.getMyProfile(),
        facilityProfileAPI.getProfessionals(),
        facilityProfileAPI.getInvitations()
      ]);
      
      const f = profileRes.data.facility;
      setFacility(f);
      setBasicInfo({
        name: f.name || '',
        description: f.description || '',
        phone: f.phone || '',
        email: f.email || '',
        website: f.website || '',
        address: f.address || '',
        city: f.city || ''
      });
      setSelectedSpecialties(f.specialties || []);
      setServices(f.services || []);
      setYoutubeUrl(f.youtube_url || '');
      setOpeningHours(f.opening_hours || {});
      setEmergency24h(f.emergency_24h || false);
      setAlwaysOpen(f.always_open || false);
      setProfessionals(profsRes.data.professionals || []);
      setInvitations(invitesRes.data.invitations || []);
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Erreur lors du chargement du profil');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = async (type, file) => {
    if (!file) return;
    
    const loadingToast = toast.loading(`Téléchargement de la photo ${type === 'profile' ? 'de profil' : 'de couverture'}...`);
    
    try {
      const response = await facilityProfileAPI.uploadPhoto(type, file);
      setFacility(prev => ({
        ...prev,
        [type === 'profile' ? 'profile_photo' : 'cover_photo']: response.data.url
      }));
      toast.dismiss(loadingToast);
      toast.success('Photo téléchargée avec succès');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleGalleryUpload = async (file) => {
    if (!file) return;
    
    const loadingToast = toast.loading('Ajout de la photo à la galerie...');
    
    try {
      const response = await facilityProfileAPI.addGalleryPhoto(file);
      setFacility(prev => ({
        ...prev,
        gallery: [...(prev.gallery || []), response.data.photo]
      }));
      toast.dismiss(loadingToast);
      toast.success('Photo ajoutée à la galerie');
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.detail || 'Erreur lors du téléchargement');
    }
  };

  const handleRemoveGalleryPhoto = async (photoId) => {
    if (!window.confirm('Supprimer cette photo ?')) return;
    
    try {
      await facilityProfileAPI.removeGalleryPhoto(photoId);
      setFacility(prev => ({
        ...prev,
        gallery: (prev.gallery || []).filter(p => p.id !== photoId)
      }));
      toast.success('Photo supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleSaveBasicInfo = async () => {
    setSaving(true);
    try {
      await facilityProfileAPI.updateProfile({
        ...basicInfo,
        emergency_24h: emergency24h,
        always_open: alwaysOpen
      });
      toast.success('Informations mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSpecialties = async () => {
    setSaving(true);
    try {
      await facilityProfileAPI.updateSpecialties(selectedSpecialties);
      toast.success('Spécialités mises à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveServices = async () => {
    setSaving(true);
    try {
      await facilityProfileAPI.updateServices(services);
      toast.success('Services mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAddService = () => {
    if (newService.trim() && !services.includes(newService.trim())) {
      setServices([...services, newService.trim()]);
      setNewService('');
    }
  };

  const handleRemoveService = (service) => {
    setServices(services.filter(s => s !== service));
  };

  const handleSaveYoutube = async () => {
    if (!youtubeUrl) {
      try {
        await facilityProfileAPI.removeYoutubeVideo();
        setFacility(prev => ({ ...prev, youtube_video_id: null, youtube_url: null }));
        toast.success('Vidéo supprimée');
      } catch (error) {
        toast.error('Erreur');
      }
      return;
    }
    
    setSaving(true);
    try {
      const response = await facilityProfileAPI.setYoutubeVideo(youtubeUrl);
      setFacility(prev => ({ ...prev, youtube_video_id: response.data.video_id, youtube_url: youtubeUrl }));
      toast.success('Vidéo YouTube ajoutée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'URL YouTube invalide');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveHours = async () => {
    setSaving(true);
    try {
      await facilityProfileAPI.updateHours(openingHours);
      toast.success('Horaires mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleInviteProfessional = async () => {
    if (!inviteEmail) return;
    
    setSaving(true);
    try {
      const response = await facilityProfileAPI.inviteProfessional(inviteEmail);
      setInvitations([response.data.invitation, ...invitations]);
      setInviteEmail('');
      setShowInviteModal(false);
      toast.success('Invitation envoyée');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveProfessional = async (associationId, name) => {
    if (!window.confirm(`Retirer ${name} de l&apos;établissement ?`)) return;
    
    try {
      await facilityProfileAPI.removeProfessional(associationId);
      setProfessionals(professionals.filter(p => p.association_id !== associationId));
      toast.success('Professionnel retiré');
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const toggleSpecialty = (specialty) => {
    if (selectedSpecialties.includes(specialty)) {
      setSelectedSpecialties(selectedSpecialties.filter(s => s !== specialty));
    } else if (selectedSpecialties.length < 20) {
      setSelectedSpecialties([...selectedSpecialties, specialty]);
    } else {
      toast.error('Maximum 20 spécialités');
    }
  };

  const updateHour = (day, field, value) => {
    setOpeningHours(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 w-full max-w-full overflow-x-hidden" data-testid="facility-profile-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-[#1A237E]" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Profil de l&apos;établissement
        </h1>
        <p className="text-slate-500 mt-1">
          Gérez les informations de votre établissement
        </p>
      </div>

      {/* Cover Photo */}
      <Card className="mb-6 overflow-hidden border-0 shadow-sm">
        <div 
          className="relative h-48 md:h-64 bg-gradient-to-r from-[#1A237E] to-[#1976D2] bg-cover bg-center"
          style={facility?.cover_photo ? { backgroundImage: `url(${facility.cover_photo})` } : {}}
        >
          <div className="absolute inset-0 bg-black/20" />
          <Button
            variant="secondary"
            size="sm"
            className="absolute bottom-4 right-4 bg-white/90 hover:bg-white"
            onClick={() => coverPhotoRef.current?.click()}
          >
            <Camera className="w-4 h-4 mr-2" />
            Modifier la couverture
          </Button>
          <input
            type="file"
            ref={coverPhotoRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => handlePhotoUpload('cover', e.target.files[0])}
          />
          
          {/* Profile Photo */}
          <div className="absolute -bottom-12 left-6">
            <div 
              className="relative w-24 h-24 md:w-32 md:h-32 rounded-xl bg-white shadow-lg border-4 border-white bg-cover bg-center"
              style={facility?.profile_photo ? { backgroundImage: `url(${facility.profile_photo})` } : {}}
            >
              {!facility?.profile_photo && (
                <div className="w-full h-full flex items-center justify-center bg-slate-100 rounded-lg">
                  <Building2 className="w-12 h-12 text-slate-400" />
                </div>
              )}
              <Button
                variant="secondary"
                size="icon"
                className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full shadow-md"
                onClick={() => profilePhotoRef.current?.click()}
              >
                <Camera className="w-4 h-4" />
              </Button>
              <input
                type="file"
                ref={profilePhotoRef}
                className="hidden"
                accept="image/*"
                onChange={(e) => handlePhotoUpload('profile', e.target.files[0])}
              />
            </div>
          </div>
        </div>
        <CardContent className="pt-16 pb-6">
          <h2 className="text-xl font-bold text-slate-900">{facility?.name}</h2>
          <p className="text-slate-500">{facility?.facility_type}</p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="info" className="rounded-lg">Informations</TabsTrigger>
          <TabsTrigger value="gallery" className="rounded-lg">Galerie</TabsTrigger>
          <TabsTrigger value="specialties" className="rounded-lg">Spécialités</TabsTrigger>
          <TabsTrigger value="hours" className="rounded-lg">Horaires</TabsTrigger>
          <TabsTrigger value="team" className="rounded-lg">Équipe</TabsTrigger>
        </TabsList>

        {/* Info Tab */}
        <TabsContent value="info">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Informations générales</CardTitle>
              <CardDescription>Les informations de base de votre établissement</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nom de l&apos;établissement</Label>
                  <Input
                    value={basicInfo.name}
                    onChange={(e) => setBasicInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Ex: Clinique du Soleil"
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <AddressAutocomplete
                    value={basicInfo.city}
                    onChange={(value) => setBasicInfo(prev => ({ ...prev, city: value }))}
                    onSelect={(location) => {
                      setBasicInfo(prev => ({ 
                        ...prev, 
                        city: location.address?.city || location.address?.town || location.display_name.split(',')[0],
                        latitude: location.lat,
                        longitude: location.lon
                      }));
                    }}
                    placeholder="Rechercher une ville..."
                    filterType="cities"
                    className="mt-1"
                    data-testid="city-autocomplete"
                  />
                </div>
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={basicInfo.description}
                  onChange={(e) => setBasicInfo(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Décrivez votre établissement..."
                  rows={3}
                />
              </div>
              
              <div>
                <Label>Adresse</Label>
                <AddressAutocomplete
                  value={basicInfo.address}
                  onChange={(value) => setBasicInfo(prev => ({ ...prev, address: value }))}
                  onSelect={(location) => {
                    setBasicInfo(prev => ({ 
                      ...prev, 
                      address: location.display_name,
                      latitude: location.lat,
                      longitude: location.lon
                    }));
                  }}
                  placeholder="Rechercher une adresse..."
                  showMap={true}
                  mapHeight="180px"
                  className="mt-1"
                  data-testid="address-autocomplete"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={basicInfo.phone}
                    onChange={(e) => setBasicInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+237 6XX XXX XXX"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={basicInfo.email}
                    onChange={(e) => setBasicInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@etablissement.com"
                  />
                </div>
                <div>
                  <Label>Site web</Label>
                  <Input
                    value={basicInfo.website}
                    onChange={(e) => setBasicInfo(prev => ({ ...prev, website: e.target.value }))}
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
                <div>
                  <p className="font-medium text-red-900">Urgences 24h/24</p>
                  <p className="text-sm text-red-700">Service d&apos;urgence disponible en permanence</p>
                </div>
                <Switch
                  checked={emergency24h}
                  onCheckedChange={setEmergency24h}
                />
              </div>
              
              {/* YouTube Video */}
              <div className="pt-4 border-t">
                <Label>Vidéo YouTube de présentation</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={youtubeUrl}
                    onChange={(e) => setYoutubeUrl(e.target.value)}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  <Button onClick={handleSaveYoutube} disabled={saving}>
                    <Video className="w-4 h-4 mr-2" />
                    {youtubeUrl ? 'Mettre à jour' : 'Ajouter'}
                  </Button>
                </div>
                {facility?.youtube_video_id && (
                  <div className="mt-4 aspect-video max-w-lg rounded-xl overflow-hidden">
                    <iframe
                      src={`https://www.youtube.com/embed/${facility.youtube_video_id}`}
                      className="w-full h-full"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
              
              <div className="flex justify-end pt-4">
                <Button onClick={handleSaveBasicInfo} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Gallery Tab */}
        <TabsContent value="gallery">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Galerie photos</CardTitle>
              <CardDescription>Ajoutez des photos de votre établissement (max 20)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {(facility?.gallery || []).map((photo) => (
                  <div key={photo.id} className="relative group aspect-square rounded-xl overflow-hidden">
                    <img src={photo.url} alt={photo.caption || 'Photo'} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <Button
                        variant="destructive"
                        size="icon"
                        onClick={() => handleRemoveGalleryPhoto(photo.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {photo.caption && (
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-xs p-2 truncate">
                        {photo.caption}
                      </div>
                    )}
                  </div>
                ))}
                
                {/* Add photo button */}
                {(facility?.gallery || []).length < 20 && (
                  <button
                    onClick={() => galleryRef.current?.click()}
                    className="aspect-square rounded-xl border-2 border-dashed border-slate-300 flex flex-col items-center justify-center hover:border-[#E53935] hover:bg-red-50 transition-colors"
                  >
                    <Plus className="w-8 h-8 text-slate-400" />
                    <span className="text-sm text-slate-500 mt-2">Ajouter</span>
                  </button>
                )}
              </div>
              <input
                type="file"
                ref={galleryRef}
                className="hidden"
                accept="image/*"
                onChange={(e) => handleGalleryUpload(e.target.files[0])}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Specialties Tab */}
        <TabsContent value="specialties">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Spécialités médicales</CardTitle>
              <CardDescription>Sélectionnez les spécialités disponibles (max 20)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2 mb-6">
                {MEDICAL_SPECIALTIES.map((specialty) => (
                  <Badge
                    key={specialty}
                    variant={selectedSpecialties.includes(specialty) ? 'default' : 'outline'}
                    className={`cursor-pointer transition-all ${
                      selectedSpecialties.includes(specialty) 
                        ? 'bg-[#1A237E] hover:bg-[#1A237E]/90' 
                        : 'hover:bg-slate-100'
                    }`}
                    onClick={() => toggleSpecialty(specialty)}
                  >
                    {specialty}
                  </Badge>
                ))}
              </div>
              
              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-slate-500">
                  {selectedSpecialties.length}/20 spécialités sélectionnées
                </p>
                <Button onClick={handleSaveSpecialties} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer
                </Button>
              </div>
              
              {/* Services Section */}
              <div className="mt-8 pt-6 border-t">
                <h3 className="font-semibold text-slate-900 mb-4">Services proposés</h3>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newService}
                    onChange={(e) => setNewService(e.target.value)}
                    placeholder="Ajouter un service (ex: Consultation à domicile)"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddService()}
                  />
                  <Button onClick={handleAddService}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {services.map((service) => (
                    <Badge key={service} variant="secondary" className="pr-1">
                      {service}
                      <button
                        onClick={() => handleRemoveService(service)}
                        className="ml-2 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex justify-end mt-4">
                  <Button onClick={handleSaveServices} disabled={saving}>
                    Enregistrer les services
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hours Tab */}
        <TabsContent value="hours">
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle>Horaires d&apos;ouverture</CardTitle>
              <CardDescription>Définissez vos heures d&apos;ouverture</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Always Open Option */}
              <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl mb-6">
                <div>
                  <p className="font-medium text-blue-900">Toujours ouvert (24h/24, 7j/7)</p>
                  <p className="text-sm text-blue-700">Votre établissement est ouvert en permanence</p>
                </div>
                <Switch
                  checked={alwaysOpen}
                  onCheckedChange={(checked) => {
                    setAlwaysOpen(checked);
                    if (checked) {
                      // Set all days to 24h
                      const always24h = {};
                      DAYS.forEach(({ key }) => {
                        always24h[key] = { open: '00:00', close: '23:59', closed: false, always_open: true };
                      });
                      setOpeningHours(always24h);
                    }
                  }}
                />
              </div>

              {/* Regular Hours (disabled if always open) */}
              {!alwaysOpen && (
                <div className="space-y-4">
                  {DAYS.map(({ key, label }) => (
                    <div key={key} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                      <div className="w-24 font-medium text-slate-700">{label}</div>
                      <Switch
                        checked={!openingHours[key]?.closed}
                        onCheckedChange={(checked) => updateHour(key, 'closed', !checked)}
                      />
                      {!openingHours[key]?.closed && (
                        <>
                          <Input
                            type="time"
                            value={openingHours[key]?.open || '08:00'}
                            onChange={(e) => updateHour(key, 'open', e.target.value)}
                            className="w-32"
                          />
                          <span className="text-slate-400">à</span>
                          <Input
                            type="time"
                            value={openingHours[key]?.close || '18:00'}
                            onChange={(e) => updateHour(key, 'close', e.target.value)}
                            className="w-32"
                          />
                        </>
                      )}
                      {openingHours[key]?.closed && (
                        <span className="text-slate-400">Fermé</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {alwaysOpen && (
                <div className="text-center py-8 bg-blue-50 rounded-xl">
                  <Clock className="w-12 h-12 text-blue-400 mx-auto mb-3" />
                  <p className="text-blue-700 font-medium">Ouvert 24h/24, 7j/7</p>
                  <p className="text-sm text-blue-600">Votre établissement est toujours accessible</p>
                </div>
              )}

              <div className="flex justify-end mt-6">
                <Button onClick={handleSaveHours} disabled={saving}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Enregistrer les horaires
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Tab */}
        <TabsContent value="team">
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Équipe médicale</CardTitle>
                <CardDescription>Professionnels associés à votre établissement</CardDescription>
              </div>
              <Button onClick={() => setShowInviteModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Inviter un professionnel
              </Button>
            </CardHeader>
            <CardContent>
              {/* Professionals List */}
              {professionals.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                  <p className="text-slate-500">Aucun professionnel associé</p>
                  <p className="text-sm text-slate-400 mt-1">Invitez des professionnels de santé à rejoindre votre établissement</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {professionals.map((prof) => (
                    <div key={prof.association_id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                      <div className="w-12 h-12 rounded-full bg-[#1A237E] flex items-center justify-center text-white">
                        {prof.profile_photo ? (
                          <img src={prof.profile_photo} className="w-full h-full rounded-full object-cover" />
                        ) : (
                          <Stethoscope className="w-6 h-6" />
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">{prof.full_name}</p>
                        <p className="text-sm text-slate-500">{prof.specialty}</p>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveProfessional(prof.association_id, prof.full_name)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Pending Invitations */}
              {invitations.filter(i => i.status === 'pending').length > 0 && (
                <div className="mt-8 pt-6 border-t">
                  <h3 className="font-semibold text-slate-900 mb-4">Invitations en attente</h3>
                  <div className="space-y-3">
                    {invitations.filter(i => i.status === 'pending').map((inv) => (
                      <div key={inv.id} className="flex items-center gap-4 p-3 bg-amber-50 rounded-xl">
                        <Mail className="w-5 h-5 text-amber-600" />
                        <div className="flex-1">
                          <p className="font-medium text-slate-900">{inv.professional_name}</p>
                          <p className="text-sm text-slate-500">{inv.professional_email}</p>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">En attente</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inviter un professionnel</DialogTitle>
            <DialogDescription>
              Entrez l&apos;email d&apos;un professionnel inscrit sur DOCKHTA
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label>Email du professionnel</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="docteur@exemple.com"
              />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setShowInviteModal(false)}>
                Annuler
              </Button>
              <Button className="flex-1 bg-[#E53935] hover:bg-[#C62828]" onClick={handleInviteProfessional} disabled={saving || !inviteEmail}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                Envoyer l&apos;invitation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FacilityProfilePage;
